/**
 * routeSprites — the RouteScene's pure, Phaser-free responsive-sprite math.
 *
 * The route's overworld sprites (player, follower, NPC trainers, wild creatures) are
 * tuned LARGE for a roomy desktop view (CLAUDE.md: "overworld sprites render ~2× larger
 * so everyone reads clearly"). In a ~390px portrait phone those desktop footprints
 * overflow the route — sprites swamp the d-pad / banner / labels — so on a compact
 * viewport every footprint is multiplied by a responsive scale and each floating label
 * is re-seated from the (smaller) rendered footprint instead of a fixed desktop lift.
 *
 * This lives apart from RouteScene.ts (which imports Phaser at module scope and so can't
 * be loaded in the node test env) so the responsive geometry stays unit-testable.
 */

/**
 * Multiplier applied to every overworld footprint (player, follower, NPC trainers, wild
 * creatures) on a NARROW/PORTRAIT phone. The desktop sprites are tuned ~2× large for a
 * roomy view; in a ~390px viewport that overflows the route, so we shrink them by a bit
 * over half. Desktop (roomy width, no notch) keeps 1.0 — see {@link computeSpriteScale}.
 */
export const COMPACT_SPRITE_SCALE = 0.55;

/**
 * The overworld sprite-footprint multiplier for the current viewport. The desktop
 * sprites are tuned large; on a narrow/portrait phone (compact width, OR a real
 * safe-area inset — a notched phone in landscape can still be cramped) they overflow the
 * route, so shrink everything to {@link COMPACT_SPRITE_SCALE}. Roomy desktop keeps 1.0.
 * Pure given the few inputs the layout snapshot exposes.
 */
export function computeSpriteScale(opts: {
  isCompact: boolean;
  insetTop: number;
  insetBottom: number;
}): number {
  const hasNotch = opts.insetTop > 0 || opts.insetBottom > 0;
  return opts.isCompact || hasNotch ? COMPACT_SPRITE_SCALE : 1;
}

/**
 * A floating-label resting offset (a NEGATIVE px lift above the sprite's feet), derived
 * from the RESPONSIVE footprint so a label hugs the (smaller) phone sprite instead of
 * floating in empty space at the fixed desktop offset. `baseOffset` / `baseSide` are the
 * desktop pair (e.g. a -78px lift over an 84px wild); we keep the label that same
 * FRACTION of the footprint above the head, then add a small fixed gap so even a tiny
 * wild's tag clears its head. Returns the desktop offset unchanged when
 * `renderedSide === baseSide`. Pure.
 */
export function labelOffsetFor(baseOffset: number, baseSide: number, renderedSide: number): number {
  const gap = 8;
  const safeBase = baseSide > 0 ? baseSide : 1;
  const frac = (Math.abs(baseOffset) - gap) / safeBase; // desktop lift as a height-fraction
  return -(renderedSide * frac + gap);
}
